import Callout from "$components/editor/extensions/blocks/Callout/Callout.svelte";
import { SvelteSSRRenderer } from "app/components/editor/utils";
import { Node, mergeAttributes, wrappingInputRule } from "@tiptap/core";
import { SvelteNodeViewRenderer } from "svelte-tiptap";
import { type MarkdownSerializerState } from "prosemirror-markdown";
import { type Node as PMNode } from "@tiptap/pm/model";

export interface CalloutOptions {
  HTMLAttributes: Record<string, any>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    callout: {
      /**
       * Set a callout node
       */
      setCallout: () => ReturnType;
      /**
       * Toggle a callout node
       */
      toggleCallout: () => ReturnType;
      /**
       * Unset a callout node
       */
      unsetCallout: () => ReturnType;
    };
  }
}

export const inputRegex = /^\s*!\s$/;

export default Node.create<CalloutOptions>({
  name: "callout",
  group: "block",
  content: "paragraph block*", // force user to add text before any blocks
  defining: true,
  draggable: true,
  addAttributes() {
    return {
      variant: {
        default: "info",
      },
    };
  },

  parseHTML() {
    return [{ tag: "callout" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return SvelteSSRRenderer("callout", Callout, {
      node,
      hasContent: true,
      attributes: mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
    });
  },

  addCommands() {
    return {
      setCallout:
        () =>
        ({ commands }) => {
          return commands.wrapIn(this.name);
        },
      toggleCallout:
        () =>
        ({ commands }) => {
          return commands.toggleWrap(this.name);
        },
      unsetCallout:
        () =>
        ({ commands }) => {
          return commands.lift(this.name);
        },
    };
  },

  addInputRules() {
    return [
      wrappingInputRule({
        find: inputRegex,
        type: this.type,
      }),
    ];
  },

  addNodeView() {
    return SvelteNodeViewRenderer(Callout);
  },

  addStorage() {
    return {
      markdown: {
        serialize: (state: MarkdownSerializerState, node: PMNode) => {
          state.ensureNewLine();

          // wrap the callout content with '> ' prefix for each line
          state.wrapBlock("> ", null, node, () => {
            state.renderContent(node);
          });

          state.closeBlock(node);

          // final format looks like this:
          // > Callout title
          // > Callout nested content
          // > Which can be multiple lines
        },
      },
    };
  },
});
